import React from 'react';
import { Chip } from '@mui/material';
import { PropTypes } from 'prop-types';
import { getLabel, convertDateValue, getSelectedStatus } from '../../utils';

export function BuildChip({ field, values, setFieldValue, resetBinders, submitSearch }) {
  const { label, name, type, radios, options, progressiveDisclosure, readOnly } = field;
  const { conditionalLabelOn } = progressiveDisclosure ?? {};
  const value = values[name];

  if (!value) return null;

  const typeHandlers = {
    select: {
      getChipValue: () => options.find((opt) => opt.value === value)?.label,
      onDelete: () => setFieldValue(name, '', false)
    },
    multiSelect: {
      getChipValue: () => {
        const selected = getSelectedStatus(options, value);
        return selected.length > 0 ? selected.join(', ') : null;
      },
      onDelete: () => setFieldValue(name, [], false)
    },
    radio: {
      getChipValue: () => radios.find((radio) => radio.value === value)?.label,
      onDelete: () => setFieldValue(name, '', false)
    },
    checkbox: {
      getChipValue: () => value.toString(),
      onDelete: () => setFieldValue(name, false, false)
    },
    dateRange: {
      getChipValue: () => (value.checkin && value.checkout ? convertDateValue(value.checkin, value.checkout) : null),
      onDelete: () => {
        setFieldValue(name, { checkin: '', checkout: '' }, false);
        resetBinders[name]?.();
      }
    },
    expiryDateRange: {
      getChipValue: () => (value.startDate && value.endDate ? convertDateValue(value.startDate, value.endDate) : null),
      onDelete: () => {
        setFieldValue(name, { startDate: '', endDate: '' }, false);
        resetBinders[name]?.();
      }
    },
    graphAutoComplete: {
      getChipValue: () => value?.query?.toString(),
      onDelete: () => setFieldValue(name, { value: '', query: '' }, false)
    },
    graphSelect: {
      getChipValue: () => value,
      onDelete: () => setFieldValue(name, '', false)
    },
    multiSearchSelect: {
      getChipValue: () => {
        const selected = getSelectedStatus(options, value);
        if (selected.length === 0) return null;
        return selected.length <= 4 ? selected.join(', ') : `${selected.length} selected`;
      },
      onDelete: () => setFieldValue(name, [], false)
    },
    default: {
      getChipValue: () => value?.toString(),
      onDelete: () => setFieldValue(name, '', false)
    }
  };

  const { getChipValue, onDelete } = typeHandlers[type] || typeHandlers.default;

  const chipValue = getChipValue();
  if (!chipValue) return null;

  const chipLabel = getLabel(conditionalLabelOn, values, label, name);
  return (
    <Chip
      {...field}
      key={name}
      name={chipLabel}
      onDelete={
        readOnly
          ? undefined
          : () => {
              onDelete();
              submitSearch();
            }
      }
      label={chipValue ? `${chipLabel}: ${chipValue}` : chipLabel}
    />
  );
}

BuildChip.propTypes = {
  theme: PropTypes.shape({}),
  field: PropTypes.shape({
    label: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
    radios: PropTypes.shape([]),
    options: PropTypes.shape([]),
    progressiveDisclosure: PropTypes.shape({}),
    readOnly: PropTypes.bool
  }),
  values: PropTypes.shape({}),
  setFieldValue: PropTypes.func,
  resetBinders: PropTypes.func,
  submitSearch: PropTypes.func
};

BuildChip.defaultProps = {
  theme: {},
  field: {},
  values: {},
  setFieldValue: () => {},
  resetBinders: () => {},
  submitSearch: () => {}
};
